// src/tokens.js — Shared design tokens, helpers, and UI components
import { useState, useEffect } from 'react';
import { Search, Bell, Settings, ShieldCheck, Menu, ChevronDown, ChevronRight } from 'lucide-react';

export const C = {
  bg:"#F6F7FA", bgAlt:"#ECEEF4", surface:"#FFFFFF",
  navy:"#1E3A5F", navyMid:"#2D5282", navyLight:"#5A82B4",
  navyFaint:"#E8EEF6", navyBorder:"#B8CCE0",
  charcoal:"#1A1A1A", charMid:"#555560", charLight:"#96969F",
  border:"#D8DCE8", borderHi:"#A8B4CC",
  green:"#1B7A4A", greenBg:"#E8F5EE", greenBd:"#A8D8BC",
  red:"#B83232", redBg:"#FAECEC", redBd:"#E0AAAA",
  amber:"#C47A10", amberBg:"#FDF4E3", amberBd:"#EAC878",
  teal:"#147070", tealBg:"#E4F2F2", tealBd:"#96CCCC",
  purple:"#5A3490", purpleBg:"#F0EAF8", purpleBd:"#B89CDC",
  gold:"#A07820", goldBg:"#FAF4E4", goldBd:"#D4B464",
  slate:"#445566", slateBg:"#EEF0F4", slateBd:"#BBC4D0",
};

export const F = {
  display:"'Cormorant Garamond',Georgia,serif",
  body:"'Jost',system-ui,sans-serif",
  mono:"'DM Mono',monospace",
};

export const fmtDate = d => {
  try {
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y,m,day] = d.split('-').map(Number);
      return new Date(y, m-1, day).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
    }
    return new Date(d).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"});
  }
  catch { return d||''; }
};

export const fmtTime = ts => {
  try { return new Date(ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}); }
  catch { return ''; }
};

export const todayLocal = (offsetDays = 0) => {
  const d = new Date();
  if (offsetDays) d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export const fmtMoney = n => `$${Number(n||0).toLocaleString()}`;

// callClaude never rejects — these two failure values ride out on the success
// channel and are strings, exactly like a real model reply. Nothing about their
// type distinguishes them from content, so any caller that PERSISTS a reply must
// compare against these constants explicitly before writing it to the record.
export const AI_ERROR = "Error reaching AI.";
export const AI_EMPTY = "No response.";

export async function callClaude(system, user, maxTokens=1500) {
  try {
    const res = await fetch("/api/claude", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ system, user, maxTokens }),
    });
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    return data.text || AI_EMPTY;
  } catch(e) {
    console.error("Claude API error:", e);
    return AI_ERROR;
  }
}

export const Wordmark = ({size="md",light=false}) => {
  const sz = {sm:[14,9,3],md:[19,11,4],lg:[28,15,6],xl:[42,20,8]}[size]||[19,11,4];
  return (
    <div style={{display:"flex",alignItems:"baseline",gap:sz[2]}}>
      <span style={{fontFamily:F.display,fontSize:sz[0],fontWeight:700,color:light?"#fff":C.charcoal,letterSpacing:"-0.01em",lineHeight:1}}>BB</span>
      <span style={{fontFamily:F.body,fontSize:sz[1],fontWeight:600,color:light?"rgba(255,255,255,.75)":C.navy,letterSpacing:"0.16em",textTransform:"uppercase",lineHeight:1}}>Integrated</span>
    </div>
  );
};

export const Card = ({children,style={},className="",...rest}) => (
  <div className={className} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,boxShadow:"0 1px 3px rgba(30,58,95,.06)",...style}} {...rest}>{children}</div>
);

export const Btn = ({children,onClick,variant="ghost",disabled,small,full,loading}) => {
  const v = {
    primary:{background:C.navy,color:"#fff",border:`1px solid ${C.navy}`,fontWeight:600},
    ghost:{background:"transparent",color:C.charMid,border:`1px solid ${C.border}`},
    outline:{background:"transparent",color:C.navy,border:`1px solid ${C.navyMid}`},
    soft:{background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,fontWeight:600},
    success:{background:C.green,color:"#fff",border:`1px solid ${C.green}`,fontWeight:600},
    danger:{background:"transparent",color:C.red,border:`1px solid ${C.redBd}`},
  };
  return (
    <button onClick={onClick} disabled={disabled||loading} style={{
      ...v[variant],borderRadius:8,padding:small?"5px 13px":"10px 20px",
      fontSize:small?11:13,opacity:(disabled||loading)?.55:1,
      width:full?"100%":"auto",letterSpacing:"0.01em",
      fontFamily:F.body,cursor:(disabled||loading)?"not-allowed":"pointer",
    }}>{loading?"Processing…":children}</button>
  );
};

export const Badge = ({label,type}) => {
  const m = {
    active:[C.navyFaint,C.navy,C.navyBorder],complete:[C.greenBg,C.green,C.greenBd],
    good:[C.greenBg,C.green,C.greenBd],low:[C.greenBg,C.green,C.greenBd],
    medium:[C.amberBg,C.amber,C.amberBd],high:[C.redBg,C.red,C.redBd],
    pending:[C.amberBg,C.amber,C.amberBd],signed:[C.greenBg,C.green,C.greenBd],
    pending_signature:[C.amberBg,C.amber,C.amberBd],draft:[C.slateBg,C.slate,C.slateBd],
    paid:[C.greenBg,C.green,C.greenBd],unpaid:[C.amberBg,C.amber,C.amberBd],
    overdue:[C.redBg,C.red,C.redBd],global:[C.goldBg,C.gold,C.goldBd],
    session:[C.navyFaint,C.navyMid,C.navyBorder],milestone:[C.tealBg,C.teal,C.tealBd],
    insight:[C.purpleBg,C.purple,C.purpleBd],
    // Inbox classifier event-type badges (see classifyTimelineEvent in Portal.js).
    // doc/project/assessment are new; session/insight/milestone reuse the entries above.
    doc:[C.greenBg,C.green,C.greenBd],project:[C.goldBg,C.gold,C.goldBd],
    assessment:[C.amberBg,C.amber,C.amberBd],
    Clarity:[C.amberBg,C.amber,C.amberBd],Insight:[C.purpleBg,C.purple,C.purpleBd],
    Alignment:[C.navyFaint,C.navy,C.navyBorder],Execution:[C.tealBg,C.teal,C.tealBd],
    Momentum:[C.greenBg,C.green,C.greenBd],
  };
  const [bg,fg,bd]=m[type]||[C.navyFaint,C.navy,C.navyBorder];
  return <span style={{background:bg,color:fg,border:`1px solid ${bd}`,borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:F.body,whiteSpace:"nowrap"}}>{label}</span>;
};

export const PhaseBar = ({n,light}) => {
  const phases=["Clarity","Insight","Alignment","Execution","Momentum"];
  return (
    <div style={{display:"flex",gap:3}}>
      {phases.map((p,i)=>(
        <div key={p} style={{flex:1}}>
          <div style={{height:3,borderRadius:2,background:i<n?(light?"rgba(255,255,255,.85)":C.navy):(light?"rgba(255,255,255,.2)":C.border)}}/>
          <div style={{fontFamily:F.mono,fontSize:7,color:i<n?(light?"rgba(255,255,255,.65)":C.navy):(light?"rgba(255,255,255,.3)":C.charLight),marginTop:3,textAlign:"center"}}>{p}</div>
        </div>
      ))}
    </div>
  );
};

export const SectionHead = ({title,sub,action}) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
    <div>
      <h2 style={{fontFamily:F.display,fontSize:22,color:C.charcoal,fontWeight:600,margin:0}}>{title}</h2>
      {sub&&<p style={{fontFamily:F.body,fontSize:13,color:C.charMid,marginTop:5}}>{sub}</p>}
    </div>
    {action}
  </div>
);

// ── Mobile detection ──────────────────────────────────────────
export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── Logo ──────────────────────────────────────────────────────
// Extracted INTEGRO span. Same styling as the existing top-bar usage.
export const Logo = ({isMobile, onClick}) => (
  <span
    onClick={onClick}
    style={{
      fontFamily:"'Josefin Sans',sans-serif",
      fontSize:isMobile?10:13,
      fontWeight:200,
      color:C.navy,
      letterSpacing:".38em",
      textTransform:"uppercase",
      lineHeight:1,
      flexShrink:0,
      cursor:onClick?"pointer":"default",
      userSelect:"none",
    }}>
    I N T E G R O
  </span>
);

// ── Mobile Hamburger ──────────────────────────────────────────
export const MobileHamburger = ({onClick}) => (
  <button onClick={onClick} style={{
    background:"none",border:`1px solid ${C.border}`,borderRadius:6,
    width:30,height:30,display:"flex",alignItems:"center",justifyContent:"center",
    cursor:"pointer",color:C.charMid,padding:0,flexShrink:0,
  }} aria-label="Open menu"><Menu size={16} strokeWidth={1.75}/></button>
);

// ── Top Bar ───────────────────────────────────────────────────
// Left: optional hamburger, logo, optional client divider + logo + name.
// Right: user identity + Sign Out. Pure layout — owns no state.
export const TopBar = ({
  onLogoClick,
  clientContext,   // null | { logoUrl, companyName, onClick }
  user,            // { name }
  roleLabel,       // 'Master' | 'Advisor' | 'Client'
  onLogout,
  onBack,          // optional — renders a "← All Clients" button
  mobileHamburger, // node | null
  isMobile,
}) => (
  <div style={{
    background:C.surface,
    borderBottom:`1px solid ${C.border}`,
    padding:isMobile?"0 14px":"0 26px",
    display:"flex",
    alignItems:"center",
    justifyContent:"space-between",
    height:isMobile?48:56,
    boxShadow:"0 1px 3px rgba(30,58,95,.06)",
    position:"sticky",
    top:0,
    zIndex:10,
  }}>
    <div style={{display:"flex",alignItems:"center",gap:isMobile?8:14,minWidth:0}}>
      {mobileHamburger}
      <Logo isMobile={isMobile} onClick={onLogoClick}/>
      {clientContext && (
        <>
          {!isMobile && <div style={{width:1,height:18,background:C.border}}/>}
          <div
            onClick={clientContext.onClick}
            style={{display:"flex",alignItems:"center",gap:8,cursor:clientContext.onClick?"pointer":"default",minWidth:0}}>
            {clientContext.logoUrl && (
              <img src={clientContext.logoUrl} alt="logo"
                style={{height:isMobile?20:26,width:"auto",objectFit:"contain",flexShrink:0}}/>
            )}
            {!isMobile && clientContext.companyName && (
              <div style={{fontFamily:F.body,fontSize:12,color:C.charLight,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                {clientContext.companyName}
              </div>
            )}
          </div>
        </>
      )}
    </div>
    <div style={{display:"flex",alignItems:"center",gap:isMobile?6:9,flexShrink:0}}>
      {onBack && !isMobile && (
        <button onClick={onBack} style={{
          background:C.navyFaint,color:C.navy,border:`1px solid ${C.navyBorder}`,
          borderRadius:7,padding:"5px 13px",fontFamily:F.body,fontSize:11,fontWeight:600,cursor:"pointer",
        }}>← All Clients</button>
      )}
      {!isMobile && user && (
        <div style={{fontFamily:F.body,fontSize:12,color:C.charcoal,fontWeight:500}}>
          {user.name}{roleLabel?` • ${roleLabel}`:""}
        </div>
      )}
      <button onClick={onLogout} style={{
        background:C.bgAlt,border:`1px solid ${C.border}`,borderRadius:6,
        padding:isMobile?"4px 8px":"5px 11px",cursor:"pointer",
        fontFamily:F.body,fontSize:isMobile?10:11,color:C.charMid,
      }}>Sign Out</button>
    </div>
  </div>
);

// ── Sidebar Item ──────────────────────────────────────────────
// Single nav row. Supports active highlight, expandable carrot, badge, nested children.
export const SidebarItem = ({
  icon: Icon,
  label,
  active,
  expandable,
  expanded,
  onToggle,
  onClick,
  badge,
  children,
  indent=0,
}) => {
  const isMobile = useIsMobile();
  const iconSize = isMobile ? 14 : 16;
  return (
    <div>
      <div
        onClick={onClick}
        onMouseEnter={e=>{if(!active) e.currentTarget.style.background=C.bgAlt;}}
        onMouseLeave={e=>{if(!active) e.currentTarget.style.background="transparent";}}
        style={{
          display:"flex",
          alignItems:"center",
          gap:10,
          padding:`8px ${10+indent*14}px`,
          margin:"1px 6px",
          borderRadius:7,
          cursor:(onClick||expandable)?"pointer":"default",
          background:active?C.navyFaint:"transparent",
          color:active?C.navy:C.charMid,
          fontFamily:F.body,
          fontSize:13,
          fontWeight:active?600:400,
          transition:"background .12s",
          userSelect:"none",
        }}>
        {Icon != null && (
          <span style={{width:18,display:"inline-flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"inherit"}}>
            <Icon size={iconSize} strokeWidth={1.75}/>
          </span>
        )}
        <span style={{flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{label}</span>
        {badge!=null && badge!==0 && (
          <span style={{
            background:C.red,color:"#fff",borderRadius:"50%",
            minWidth:18,height:18,padding:"0 5px",
            display:"inline-flex",alignItems:"center",justifyContent:"center",
            fontFamily:F.mono,fontSize:9,fontWeight:700,
          }}>{badge}</span>
        )}
        {expandable && (
          <span
            onClick={e=>{e.stopPropagation(); onToggle&&onToggle();}}
            style={{display:"inline-flex",alignItems:"center",color:C.charLight,padding:"0 4px",cursor:"pointer"}}>
            {expanded ? <ChevronDown size={14} strokeWidth={1.75}/> : <ChevronRight size={14} strokeWidth={1.75}/>}
          </span>
        )}
      </div>
      {expandable && expanded && children && <div>{children}</div>}
    </div>
  );
};

// ── Sidebar ───────────────────────────────────────────────────
// Built-in chrome: Search, Inbox, divider, role-specific children, Settings pinned bottom.
// Mobile: slide-over with backdrop. Desktop: fixed-width column.
export const Sidebar = ({
  unreadCount=0,
  onSearchSubmit,
  onInboxClick,
  inboxActive,
  showAdmin=false,
  onAdminClick,
  adminActive,
  onSettingsClick,
  settingsActive,
  isMobile,
  mobileOpen,
  onMobileClose,
  children,
}) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const body = (
    <div style={{
      width:isMobile?280:240,
      background:C.surface,
      borderRight:isMobile?"none":`1px solid ${C.border}`,
      height:"100%",
      display:"flex",
      flexDirection:"column",
      flexShrink:0,
    }}>
      <div style={{padding:"10px 0 4px"}}>
        {searchOpen ? (
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",margin:"0 12px",borderRadius:7,border:`1px solid ${C.border}`,background:C.bgAlt}}>
            <span style={{color:C.charLight,display:"inline-flex",alignItems:"center"}}><Search size={14} strokeWidth={1.75}/></span>
            <input
              autoFocus
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              onKeyDown={e=>{
                if(e.key==="Enter"&&onSearchSubmit) onSearchSubmit(searchQuery);
                if(e.key==="Escape"){setSearchOpen(false);setSearchQuery("");}
              }}
              placeholder="Search…"
              style={{flex:1,background:"transparent",border:"none",outline:"none",fontFamily:F.body,fontSize:13,color:C.charcoal,minWidth:0}}/>
            <button onClick={()=>{setSearchOpen(false);setSearchQuery("");}}
              style={{background:"none",border:"none",color:C.charLight,fontSize:11,cursor:"pointer"}}>✕</button>
          </div>
        ) : (
          <SidebarItem icon={Search} label="Search" onClick={()=>setSearchOpen(true)}/>
        )}
      </div>
      <SidebarItem icon={Bell} label="Inbox" active={inboxActive} onClick={onInboxClick} badge={unreadCount>0?unreadCount:null}/>
      <div style={{height:1,background:C.border,margin:"8px 14px"}}/>
      <div style={{flex:1,overflowY:"auto"}}>{children}</div>
      <div style={{borderTop:`1px solid ${C.border}`,padding:"6px 0"}}>
        {showAdmin && <SidebarItem icon={ShieldCheck} label="Admin" active={adminActive} onClick={onAdminClick}/>}
        <SidebarItem icon={Settings} label="Settings" active={settingsActive} onClick={onSettingsClick}/>
      </div>
    </div>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <div style={{position:"fixed",inset:0,zIndex:60,display:"flex"}}>
        <div onClick={onMobileClose} style={{position:"absolute",inset:0,background:"rgba(0,0,0,.4)"}}/>
        <div style={{position:"relative",height:"100%",boxShadow:"0 8px 24px rgba(30,58,95,.2)"}}>{body}</div>
      </div>
    );
  }
  return body;
};

// ── Breadcrumb ────────────────────────────────────────────────
export const Breadcrumb = ({segments=[]}) => {
  if (!segments.length) return null;
  return (
    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",fontFamily:F.body,fontSize:12,marginBottom:8}}>
      {segments.map((seg,i)=>{
        const isLast = i===segments.length-1;
        const clickable = !isLast && typeof seg.onClick==="function";
        return (
          <span key={i} style={{display:"flex",alignItems:"center",gap:6}}>
            <span
              onClick={clickable?seg.onClick:undefined}
              style={{
                cursor:clickable?"pointer":"default",
                color:isLast?C.charMid:C.charLight,
                fontWeight:isLast?500:400,
              }}>
              {seg.label}
            </span>
            {!isLast && <span style={{color:C.charLight}}>›</span>}
          </span>
        );
      })}
    </div>
  );
};

// ── Page Header ───────────────────────────────────────────────
// Breadcrumb + title + subtitle + optional action slot.
// If title/subtitle/action are all empty, only the breadcrumb is rendered —
// useful for tab-content pages that own their own headings.
export const PageHeader = ({breadcrumb, title, subtitle, action, rightContent}) => {
  const hasTitleRow = title || subtitle || action;
  const hasBreadcrumb = breadcrumb && breadcrumb.length>0;
  return (
    <div style={{marginBottom:hasTitleRow?24:12}}>
      {(hasBreadcrumb || rightContent) && (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:16}}>
          {hasBreadcrumb ? <Breadcrumb segments={breadcrumb}/> : <div/>}
          {rightContent && <div style={{flexShrink:0}}>{rightContent}</div>}
        </div>
      )}
      {hasTitleRow && (
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:16,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:0}}>
            {title && <div style={{fontFamily:F.display,fontSize:26,color:C.charcoal,fontWeight:600,marginBottom:4,lineHeight:1.2}}>{title}</div>}
            {subtitle && <div style={{fontFamily:F.body,fontSize:13,color:C.charMid}}>{subtitle}</div>}
          </div>
          {action && <div style={{flexShrink:0}}>{action}</div>}
        </div>
      )}
    </div>
  );
};

// ── Impersonation Banner ──────────────────────────────────────
// Mirrors the existing yellow banner used for "Previewing as Client".
// Designed to sit ABOVE the TopBar.
export const ImpersonationBanner = ({label, exitLabel="Exit", onExit}) => (
  <div style={{
    background:"#FFF3CD",
    borderBottom:"2px solid #F0C040",
    padding:"10px 26px",
    display:"flex",alignItems:"center",justifyContent:"space-between",
    position:"sticky",top:0,zIndex:21,
  }}>
    <span style={{fontFamily:F.body,fontSize:13,color:"#7A5800",fontWeight:600}}>👁 {label}</span>
    <button onClick={onExit} style={{
      background:"#7A5800",color:"#fff",border:"none",borderRadius:7,
      padding:"6px 16px",fontFamily:F.body,fontSize:12,fontWeight:600,cursor:"pointer",
    }}>{exitLabel}</button>
  </div>
);

// ── Nav state ─────────────────────────────────────────────────
// Single source of truth for "which section is active" within a shell.
// useState under the hood so this can grow into a reducer later without
// changing call sites.
export function useNavState(initial) {
  const [section, setSection] = useState(initial);
  return { section, navigate: setSection };
}

// ── App Shell ─────────────────────────────────────────────────
// Composes TopBar + Sidebar + content. Pure layout — owns no business
// state besides the mobile drawer.
export function AppShell({
  user, roleLabel, clientContext, onLogout, onBack,
  sidebarItems, currentSection, onNavigate,
  unreadCount = 0, banner = null, pageHeader, stickyHeader = null,
  showAdmin = false, hasBottomNav = false, children,
}) {
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const go = (id) => { onNavigate(id); setMobileNavOpen(false); };

  return (
    <div style={{height:"100vh",background:C.bg,display:"flex",flexDirection:"column"}}>
      <style>{`input,textarea,select{font-family:${F.body};}`}</style>
      {banner}
      <TopBar
        user={user}
        roleLabel={roleLabel}
        clientContext={clientContext}
        onLogout={onLogout}
        onBack={onBack}
        onLogoClick={() => sidebarItems[0] && go(sidebarItems[0].id)}
        mobileHamburger={isMobile ? <MobileHamburger onClick={() => setMobileNavOpen(true)}/> : null}
        isMobile={isMobile}
      />
      {/* Mobile back bar — full-width strip directly under TopBar, only when
          a parent has provided onBack (i.e. master/advisor is inside a client
          portal). Replaces the tiny mobile "←" glyph that used to live in
          TopBar's right cluster and was hard to tap. */}
      {onBack && isMobile && (
        <button
          onClick={onBack}
          aria-label="Back to dashboard"
          style={{
            width:"100%",
            background:C.navyFaint,
            color:C.navy,
            border:"none",
            borderBottom:`1px solid ${C.navyBorder}`,
            height:48,
            display:"flex",
            alignItems:"center",
            gap:8,
            padding:"0 18px",
            fontFamily:F.body,
            fontSize:14,
            fontWeight:600,
            cursor:"pointer",
            textAlign:"left",
          }}>
          <span>←</span>
          <span>Back to dashboard</span>
        </button>
      )}
      <div style={{flex:1,display:"flex",minHeight:0}}>
        <Sidebar
          unreadCount={unreadCount}
          onInboxClick={() => go('inbox')}
          inboxActive={currentSection === 'inbox'}
          showAdmin={showAdmin}
          onAdminClick={() => go('admin')}
          adminActive={currentSection === 'admin'}
          onSettingsClick={() => go('settings')}
          settingsActive={currentSection === 'settings'}
          isMobile={isMobile}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}>
          {sidebarItems.map(item => (
            <SidebarItem
              key={item.id}
              icon={item.icon}
              label={item.label}
              badge={item.badge}
              active={currentSection === item.id}
              onClick={() => go(item.id)}
            />
          ))}
        </Sidebar>
        <main style={{
          flex:1,minWidth:0,overflowY:"auto",
          // Reserve space at the bottom of the scroll area for the fixed
          // mobile BottomNav so page content can scroll fully into view
          // instead of being hidden under the nav.
          ...(hasBottomNav && isMobile
            ? {paddingBottom:"calc(72px + env(safe-area-inset-bottom))"}
            : null),
        }}>
          {stickyHeader}
          <div style={{maxWidth:1100,margin:"0 auto",padding:isMobile?"20px 14px":"30px 28px"}}>
            {pageHeader && <PageHeader {...pageHeader}/>}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
